import styles from "./index.module.css";
import "./index.css";

import React from "react";
import DataComponent, {executeComponentCallback} from 'Core/components/DataComponent';
import PropTypes from "prop-types";
import {getBool, getNumber, getString, isset, trimArray} from 'Core/helpers/data';
import DataValueValidation, {ValidationRuleObject} from 'Core/validation';
import FormWrapper, {FormField} from 'Core/components/advanced/FormWrapper';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import CustomerGroupSelectInput from 'Components/input/CustomerGroupSelectInput';
import Label from 'Core/components/display/Label';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import {icon_font_create_symbol, icon_font_stack_class} from 'Config/app';
import {hideLoading, showLoading} from 'Core/helpers/loading';
import {waitingFunction} from 'Core/helpers/function';
import {addSuccessMessage} from 'Core/helpers/message';
import ConfirmDialog from 'Core/components/dialogs/ConfirmDialog';
import {closeDialog, openDialog} from 'Core/helpers/dialog';
import {CUSTOMER_GROUP_STATUS} from 'Pages/apps/default/customerGroups/const';
import CustomerGroupPreviewDialog from 'Components/dialogs/CustomerGroupPreviewDialog';
import {cloneDeep} from 'lodash';

class ImportCustomersFromGroup extends DataComponent {
	constructor(props) {
		super(props, {
			data: {
				/**
				 * Selected customer group
				 * @type {?CustomerGroupsListItemDataObject}
				 */
				customerGroup: (isset(props.defaultData) ? cloneDeep(props.defaultData) : null),
			},
			
			/**
			 * Flag that specifies if component is collapsed (input is not visible)
			 * @note This is relevant only if 'collapsable' prop is set to true.
			 * @type {boolean}
			 */
			collapsed: getBool(props, 'defaultCollapsed'),
		}, {
			translationPath: 'ImportCustomersFromGroup',
			domPrefix: 'import-customers-from-group',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onSuccess', 'onError', 'onEnterKey', 'onChange'],
		});
		
		// Refs
		this.groupSelectRef = null;

		// Dialog methods
		this.openPreviewDialog = this.openPreviewDialog.bind(this);
		
		// Action methods
		this.submit = this.submit.bind(this);
		
		// Render methods
		this.renderSelect = this.renderSelect.bind(this);
		this.renderActions = this.renderActions.bind(this);
		this.renderContent = this.renderContent.bind(this);
	}

	
	// Data methods -----------------------------------------------------------------------------------------------------
	/** @inheritDoc */
	getDataToReturn() {
		return getString(this.state, 'data.customerGroup.id');
	}


	// Validation methods -----------------------------------------------------------------------------------------------
	/** @inheritDoc */
	validate() {
		const {showSubmitButton} = this.props;
		const dataToValidate = this.getData();
		const dataValidation = new DataValueValidation();

		// Add custom rule translations
		dataValidation.addTranslation('group_inactive', this.t('customer_group_inactive_error'));
		
		// Define rules
		/** @type {Array<ValidationRuleObject|string>} */
		let customerGroupRules = [
			new ValidationRuleObject('custom', {
				validate: value => {
					if (!value || getString(value, 'status') === CUSTOMER_GROUP_STATUS.ACTIVE) return {passed: true};
					return {passed: false, error: 'group_inactive'};
				}
			})
		];
		if (showSubmitButton) customerGroupRules.push('required');
		
		// Add rules
		dataValidation.addRule('customerGroup', ...customerGroupRules);

		// Run validation
		const validationErrors = dataValidation.run(dataToValidate);
		if (validationErrors) this.setValidationErrors('', validationErrors).then();
		else this.clearValidationErrors().then();
		return !validationErrors;
	}


	// Dialog methods ---------------------------------------------------------------------------------------------------
	/**
	 * Open a dialog to preview the customer group
	 */
	async openPreviewDialog() {
		const {
			projectId, successMessage, loadingSelector, action, actionArgs, collapsable, showSubmitButton, 
		} = this.props;
		const {collapsed} = this.state;

		// Reload group select options to update the statuses before validation because status might have changed and 
		// validation will check the status and won't pass if it is not ACTIVE
		if (this.groupSelectRef) await this.groupSelectRef.loadOptions();
		
		// Validate group conditions
		if (!this.validate()) return;
		
		const dialogGUIID = openDialog('', CustomerGroupPreviewDialog, {
			showSubmitButton,
			projectId,
			customerGroup: this.getValue('customerGroup'),
			onSave: customerGroupId => {
				let loading;
				if (loadingSelector) loading = showLoading(loadingSelector);
				this.executeAbortableAction(action, {...actionArgs, customerGroupId})
					.then(res => {
						if (res) {
							if (successMessage) addSuccessMessage(successMessage);

							// Clear conditions
							this.clearData().then();

							if (collapsable && !collapsed) this.setState({collapsed: true}).then();
							executeComponentCallback(this.props.onSuccess);
						} else {
							executeComponentCallback(this.props.onError);
							return Promise.resolve();
						}
					})
					.finally(() => {
						if (loading) hideLoading(loading);
						closeDialog(dialogGUIID);
					});
			},
		}, {
			id: 'customer-group-preview-dialog',
			closeOnEscape: true,
			closeOnClickOutside: true,
			hideCloseBtn: false,
			maxWidth: 1280
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}
	

	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Submit the customer group to the action defined in props
	 */
	async submit() {
		const {collapsable, loadingSelector, confirmSubmitMessage, successMessage, action, actionArgs} = this.props;
		const {collapsed} = this.state;

		// Reload group select options to update the statuses before validation because status might have changed and 
		// validation will check the status and won't pass if it is not ACTIVE
		if (this.groupSelectRef) await this.groupSelectRef.loadOptions();
		
		// Validate segment conditions
		if (!this.validate()) return;

		const dialogGUIID = openDialog('', ConfirmDialog, {
			message: confirmSubmitMessage ? confirmSubmitMessage : this.t('confirm_submit'),
			onYes: () => {
				let loading;
				if (loadingSelector) loading = showLoading(loadingSelector);
				this.executeAbortableAction(action, {
					...actionArgs,
					customerGroupId: getString(this.getValue('customerGroup'), 'id')
				})
					.then(res => {
						if (res) {
							if (successMessage) addSuccessMessage(successMessage);

							// Clear conditions
							this.clearData().then();
							
							if (collapsable && !collapsed) this.setState({collapsed: true}).then();
							executeComponentCallback(this.props.onSuccess);
						} else {
							executeComponentCallback(this.props.onError);
							return Promise.resolve();
						}
					})
					.finally(() => { 
						if (loading) hideLoading(loading);
						closeDialog(dialogGUIID);
					});
			}
		}, {
			id: 'import-from-segment-confirm-dialog',
			closeOnEscape: true,
			closeOnClickOutside: true,
			hideCloseBtn: true,
			maxWidth: 600
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Renders a selection field for choosing a customer group
	 * @return {JSX.Element} A JSX representation of the selection field.
	 */
	renderSelect() {
		const {projectId, showInputLabel, showSubmitButton, readOnly} = this.props;

		return (
			<FormField
				required={showSubmitButton}
				label={this.t('customer_group_label')}
				labelPosition={showInputLabel ? FORM_FIELD_LABEL_POSITION.STACKED : FORM_FIELD_LABEL_POSITION.NONE}
				errorMessages={this.getValidationErrors('customerGroup')}
			>
				{readOnly ?
					<CustomerGroupSelectInput
						isSearchable={false}
						menuIsOpen={false}
						isClearable={false}
						openMenuOnClick={false}
						projectId={projectId}
						simpleValue={false}
						value={this.getValue('customerGroup')}
						onChange={v => this.handleValueChange('customerGroup', v)}
						placeholder={this.t('customer_group_placeholder')}
						ref={node => { this.groupSelectRef = node; }}
					/>
					:
					<CustomerGroupSelectInput
						projectId={projectId}
						simpleValue={false}
						isClearable={true}
						value={this.getValue('customerGroup')}
						onChange={v => this.handleValueChange('customerGroup', v)}
						placeholder={this.t('customer_group_placeholder')}
						ref={node => { this.groupSelectRef = node; }}
					/>
				}
			</FormField>
		);
	}

	/**
	 * Render actions toolbar
	 * @return {JSX.Element} A JSX representation of the action toolbar.
	 */
	renderActions() {
		const {buttonLabel, buttonProps, showSubmitButton, readOnly} = this.props;
		const customerGroup = this.getValue('customerGroup');
		const count = getNumber(customerGroup, 'customerCount');
		const disabled = (count < 1);
		
		return (
			<div className="import-actions">
				<Button
					className="preview-group-btn"
					icon="search-document"
					iconProps={{symbolPrefix: 'icofont-'}}
					label={this.t('preview_btn')}
					displayStyle={BUTTON_STYLE.SUBTLE}
					displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
					disabled={disabled}
					onClick={() => {
						const loading = showLoading('.import-actions .preview-group-btn');
						setTimeout(() => {
							waitingFunction(() => {
								const {updatingData} = this.state;
								if (!updatingData) {
									this.openPreviewDialog().then();
									return true;
								}
							}, 1, Infinity).then(() => hideLoading(loading));
						});
					}}
				/>
				
				{!readOnly && showSubmitButton ?
					<Button
						icon={icon_font_create_symbol}
						label={buttonLabel ? buttonLabel : this.t('import_from_customer_group')}
						displayStyle={BUTTON_STYLE.HIGHLIGHT}
						{...buttonProps}
						onClick={() => this.submit()}
						disabled={disabled}
					/>
					: null
				}
			</div>
		);
	}
	
	/**
	 * Render the component content
	 * @description This render method is used to render the actual content of the component. This is done with a
	 * separate method to avoid duplicating code because the component can have a collapse wrapper.
	 * @return {React.JSX.Element}
	 */
	renderContent() {
		const {readOnly} = this.props;
		
		return (
			<FormWrapper 
				id={this.getDomId()} 
				className={`${this.getOption('domPrefix')} ${styles['wrapper']} ${readOnly ? 'read-only' : ''}`}
			>
				{this.renderSelect()}
				{this.renderActions()}
			</FormWrapper>
		);
	}
	
	render() {
		const {collapsable, collapseWrapperClassName, collapseWrapperTitle} = this.props;
		const {collapsed} = this.state;
		
		return (
			collapsable ?
				<div className={`from-customer-group card ${styles['card']} ${getString(collapseWrapperClassName)}`}>
					<div
						className={`card-header with-toolbar no-select ${styles['cardHeader']}`}
						onClick={() => this.setState({collapsed: !collapsed})}
					>
						<Label
							element="span"
							elementProps={{className: 'card-title'}}
							icon={['users', 'circle-thin']}
							iconStackClass={`${icon_font_stack_class} user-groups`}
							content={(typeof collapseWrapperTitle === 'string' ? collapseWrapperTitle : this.t('title'))}
						/>
						<div className="toolbar">
							<Button
								icon={collapsed ? 'caret-down' : 'caret-up'}
								displayStyle={BUTTON_STYLE.SUBTLE}
								displayType={BUTTON_DISPLAY_TYPE.NONE}
							/>
						</div>
					</div>
					<div className={`card-content ${styles['cardContent']} ${collapsed ? 'no-display' : ''}`}>
						{this.renderContent()}
					</div>
				</div>
				:
				this.renderContent()
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
ImportCustomersFromGroup.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,

	readOnly: PropTypes.bool,
	loadingSelector: PropTypes.string,
	showInputLabel: PropTypes.bool,
	buttonLabel: PropTypes.string,
	buttonProps: PropTypes.object,
	confirmSubmitMessage: PropTypes.string,
	successMessage: PropTypes.string,
	projectId: PropTypes.string,
	collapsable: PropTypes.bool,
	defaultCollapsed: PropTypes.bool,
	collapseWrapperClassName: PropTypes.string,
	collapseWrapperTitle: PropTypes.string,
	/* @type {CustomerGroupsListItemDataObject} */
	defaultData: PropTypes.object,
	
	showSubmitButton: PropTypes.bool,
	action: PropTypes.func,
	actionArgs: PropTypes.object,

	onSuccess: PropTypes.func, // No arguments
	onError: PropTypes.func, // No arguments
};

/**
 * Define component default values for own props
 */
ImportCustomersFromGroup.defaultProps = {
	id: '',
	className: '',
	readOnly: false,
	showInputLabel: true,
	buttonProps: {},
	collapsable: false,
	defaultCollapsed: true,
	showSubmitButton: true,
};

export default ImportCustomersFromGroup;